import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Subject, firstValueFrom, takeUntil } from 'rxjs'
import { ToastService } from '@common/services'
import { AlertifyService } from '@services/alertify.service'
import { ElectronicProcessPaginationService } from '@services/electronic-process-pagination.service'
import { ElectronicProcessPaginationSocketService } from '@services/socket/electronic-process-pagination-socket.service'
import { RealtimeFallbackPoller } from '@services/realtime/realtime-fallback-poller'
import { ElectronicProcessPaginationRun, ElectronicProcessPaginationRunStatus } from '@models/planning/electronic-process-pagination.model'

/** Como o modal termina: `applied` quando a renumeração foi aplicada. */
export type ElectronicProcessPaginationSimulationResult = 'applied' | 'closed'

/**
 * Simulação de renumeração (spec 002, RF 14 a 18): acompanha a contagem e, ao terminar, mostra as folhas antes e
 * depois e as mudanças, ou as falhas. A aplicação pede confirmação explícita e usa o resultado guardado, sem contar de
 * novo. Até lá o processo segue publicado com a paginação atual.
 */
@Component({
	selector: 'app-electronic-process-pagination-simulation',
	templateUrl: './electronic-process-pagination-simulation.component.html',
	standalone: false
})
export class ElectronicProcessPaginationSimulationComponent implements OnInit, OnDestroy {
	@Input() processId: string
	@Input() run: ElectronicProcessPaginationRun

	/** Motivo para não aplicar agora (alterações não salvas na tela, por exemplo). */
	@Input() applyBlockedReason: string | null = null

	applying = false

	readonly Status = ElectronicProcessPaginationRunStatus

	private poller?: RealtimeFallbackPoller
	private destroy$ = new Subject<void>()

	constructor(
		public activeModal: NgbActiveModal,
		private toast: ToastService,
		private alertify: AlertifyService,
		private paginationService: ElectronicProcessPaginationService,
		private paginationSocket: ElectronicProcessPaginationSocketService
	) {}

	ngOnInit(): void {
		this.paginationSocket.events$.pipe(takeUntil(this.destroy$)).subscribe(run => {
			if (run.id === this.run?.id) this.run = run
		})

		// Sem conexão, a contagem segue por REST; conectado, o poller dorme.
		this.poller = new RealtimeFallbackPoller({
			isConnected: () => this.paginationSocket.connected || !this.run?.active,
			resync: () => void this.refresh().finally(() => this.poller?.settle())
		})
		this.poller.start()
	}

	ngOnDestroy(): void {
		this.poller?.stop()
		this.destroy$.next()
		this.destroy$.complete()
	}

	get unchanged(): boolean {
		return this.run?.status === ElectronicProcessPaginationRunStatus.COMPLETED
	}

	apply(): void {
		if (!this.run?.applicable || this.applying) return

		if (this.applyBlockedReason) {
			this.toast.warn(this.applyBlockedReason)
			return
		}

		this.alertify.confirm(
			'Aplicar renumeração',
			`As folhas de ${this.run.changes?.length ?? 0} arquivo(s) vão mudar e o processo passa de ${this.run.pagesBefore ?? 0} para ${this.run.pagesAfter ?? 0} folhas. A nova paginação substitui a anterior de uma vez. Deseja aplicar?`,
			event => {
				if (event.confirmed) void this.confirmApply()
			},
			{ ok: 'Aplicar renumeração', cancel: 'Voltar' }
		)
	}

	close(): void {
		this.activeModal.close('closed' as ElectronicProcessPaginationSimulationResult)
	}

	private async confirmApply(): Promise<void> {
		this.applying = true

		try {
			await firstValueFrom(this.paginationService.apply(this.processId, this.run.id))
			this.toast.success('Renumeração aplicada. As folhas do processo foram atualizadas.')
			this.activeModal.close('applied' as ElectronicProcessPaginationSimulationResult)
		} catch {
			// O 409 já mostrou o motivo (vencida, processo alterado, já aplicada): atualiza para a tela refletir o estado.
			await this.refresh()
		} finally {
			this.applying = false
		}
	}

	private async refresh(): Promise<void> {
		if (!this.processId || !this.run?.id) return

		try {
			this.run = await firstValueFrom(this.paginationService.detail(this.processId, this.run.id))
		} catch {
			/* o próximo ciclo tenta de novo */
		}
	}
}
